import { Link } from "react-router";
import { useRecordContext, useTranslate } from "ra-core";
import { Copy } from "lucide-react";
import { DateField } from "@/components/admin/date-field";
import { FileField } from "@/components/admin/file-field";
import { NumberField } from "@/components/admin/number-field";
import { RecordField } from "@/components/admin/record-field";
import { ReferenceField } from "@/components/admin/reference-field";
import { EditButton } from "@/components/admin/edit-button";
import { Show } from "@/components/admin/show";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TransactionDirectionBadge } from "../shared/TransactionDirectionBadge";
import type { Transaction } from "../../types";

const DuplicateButton = () => {
  const translate = useTranslate();
  const record = useRecordContext<Transaction>();
  if (!record) return null;
  return (
    <Button asChild variant="outline" size="sm" className="gap-2">
      <Link to={`/transactions/create?duplicate_from=${record.id}`}>
        <Copy className="h-4 w-4" />
        {translate("resources.transactions.action.duplicate", { _: "Дублировать" })}
      </Link>
    </Button>
  );
};

export const TransactionShow = () => (
  <Show actions={<div className="flex justify-end gap-2"><DuplicateButton /><EditButton /></div>}>
    <Card>
      <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <RecordField
          source="direction"
          render={(r) => (
            <TransactionDirectionBadge
              direction={r.direction}
              isTransfer={!!r.transfer_id}
            />
          )}
        />
        <RecordField source="amount" field={NumberField} />
        <RecordField source="account_id">
          <ReferenceField source="account_id" reference="accounts" link="show" />
        </RecordField>
        <RecordField source="category_id">
          <ReferenceField
            source="category_id"
            reference="transaction_categories"
            link={false}
          />
        </RecordField>
        <RecordField source="occurred_at" field={DateField} />
        <RecordField source="contract_id">
          <ReferenceField
            source="contract_id"
            reference="contracts"
            link="show"
          />
        </RecordField>
        <RecordField source="company_id">
          <ReferenceField
            source="company_id"
            reference="companies"
            link="show"
          />
        </RecordField>
        <RecordField source="deal_id">
          <ReferenceField source="deal_id" reference="deals" link="show" />
        </RecordField>
        <RecordField source="payment_id">
          <ReferenceField
            source="payment_id"
            reference="payments"
            link="show"
          />
        </RecordField>
        <RecordField source="description" className="md:col-span-2" />
        <RecordField source="attachments" className="md:col-span-2" render={(record) => (
          record.attachments && Array.isArray(record.attachments) && record.attachments.length > 0 ? (
            <ul className="space-y-1">
              {record.attachments.map((a: { src?: string; title?: string }, i: number) => (
                <li key={i}>
                  <FileField
                    record={a}
                    source="src"
                    title="title"
                    target="_blank"
                    className="[&_a]:text-blue-600 [&_a]:hover:text-blue-800 [&_a]:underline [&_a]:break-all"
                  />
                </li>
              ))}
            </ul>
          ) : (
            <span className="text-muted-foreground text-sm">—</span>
          )
        )} />
      </CardContent>
    </Card>
  </Show>
);
